import { createContext, useContext, useMemo, ReactNode } from 'react';
import styled from 'styled-components';

type AccordionContextValue = {
  activeEventKey: number | null;
  onToggle: (eventKey: number | null) => void;
};

const AccordionContext = createContext<AccordionContextValue | undefined>(undefined);

export function useAccordion(): AccordionContextValue {
  const context = useContext(AccordionContext);
  if (context === undefined) {
    throw new Error('useAccordion must be used within an Accordion component');
  }
  return context;
}

type AccordionClick = () => void;

export function useAccordionClick(eventKey: number): AccordionClick {
  const { onToggle, activeEventKey } = useAccordion();

  return () => {
    onToggle(eventKey === activeEventKey ? null : eventKey);
  };
}

const AccordionWrapper = styled.div`
  width: 100%;
`;

type AccordionProviderProps = {
  activeEventKey: number | null;
  onToggle: (eventKey: number | null) => void;
  children: ReactNode;
};

function AccordionProvider({ activeEventKey, onToggle, children }: AccordionProviderProps): JSX.Element {
  const value = useMemo(
    () => ({
      activeEventKey,
      onToggle,
    }),
    [activeEventKey, onToggle],
  );

  return (
    <AccordionContext.Provider value={value}>
      <AccordionWrapper>{children}</AccordionWrapper>
    </AccordionContext.Provider>
  );
}

export default AccordionProvider;
